/**
 * `/answer` — Answer module home page (M-ANSWER.3).
 *
 * Launchpad for the three primary surfaces: questionnaires, documents
 * (knowledge base), library. Mirrors the Quote home page shape so
 * navigation feels consistent across modules.
 */
import { BookOpenCheck, FileText, ListChecks } from "lucide-react";
import { Link } from "react-router-dom";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

export function AnswerHomePage() {
  return (
    <div className="flex flex-col gap-6">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-1">Workspace</p>
        <h1 className="text-2xl font-extrabold tracking-tight text-gray-900">Answer</h1>
        <p className="text-sm leading-relaxed text-muted-foreground">
          AI questionnaire drafting — upload your knowledge base, import questionnaires, get
          drafts with citations, review, and reuse approved answers via the library.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        <AnswerTile
          to="/answer/questionnaires"
          icon={<ListChecks className="h-5 w-5" />}
          title="Questionnaires"
          description="Create, draft, and review responses. Generate produces drafts with citations from your KB."
        />
        <AnswerTile
          to="/answer/documents"
          icon={<FileText className="h-5 w-5" />}
          title="Knowledge base"
          description="Upload PDFs, DOCX, or text files. Chunked + embedded for RAG retrieval at draft time."
        />
        <AnswerTile
          to="/answer/library"
          icon={<BookOpenCheck className="h-5 w-5" />}
          title="Answer library"
          description="Reusable approved answers. Library hits short-circuit the LLM call for matching questions."
        />
      </div>
    </div>
  );
}

function AnswerTile({
  to,
  icon,
  title,
  description,
}: {
  to: string;
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <Link
      to={to}
      className="block rounded outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
    >
      <Card className="h-full transition-colors hover:bg-gray-50">
        <CardHeader>
          <div className="mb-2 flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary">
            {icon}
          </div>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
      </Card>
    </Link>
  );
}
